import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Form, Alert, Card, Spinner } from 'react-bootstrap';
import { PASSWORD_MIN_LENGTH, isValidEmail, normalizeEmail } from '@shared/auth';
import { Gear, KeyFill, EnvelopeFill, ShieldLockFill } from 'react-bootstrap-icons';

interface RegisterProps {
  onSwitchToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();

  /** The same rules the Service applies (Shared/src/auth.ts), checked before calling it. */
  const clientError = (): string | null => {
    if (!isValidEmail(normalizeEmail(email))) return 'Please enter a valid email address';
    if (password.length < PASSWORD_MIN_LENGTH) {
      return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
    }
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError('');

    const invalid = clientError();
    if (invalid) {
      setError(invalid);
      return;
    }

    setBusy(true);
    try {
      await register(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm-auth">
      {/* Background decorative gears */}
      <div className="sm-auth__bg-gear sm-auth__bg-gear--top-left">
        <Gear size={300} className="sm-animate-gear" />
      </div>
      <div className="sm-auth__bg-gear sm-auth__bg-gear--bottom-right">
        <Gear size={250} className="sm-animate-gear sm-animate-gear--reverse" />
      </div>
      <div className="sm-auth__bg-gear sm-auth__bg-gear--center">
        <Gear size={600} className="sm-animate-gear sm-animate-gear--slow" />
      </div>

      <Card className="sm-auth__card">
        {/* Decorative elements */}
        <div className="sm-auth__card-line sm-auth__card-line--top" />
        <div className="sm-auth__rivet sm-auth__rivet--left" />
        <div className="sm-auth__rivet sm-auth__rivet--right" />
        
        <Card.Body className="sm-auth__card-body">
          <div className="sm-auth__header">
            <Gear size={48} className="sm-auth__icon" />
            <h2 className="sm-auth__title">Forge Your Account</h2>
            <p className="sm-auth__subtitle">
              Or{' '}
              <button type="button" className="sm-auth__link" onClick={onSwitchToLogin}>
                enter existing forge
              </button>
            </p>
          </div>

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="sm-auth__form-group">
              <Form.Label className="sm-auth__label">
                <EnvelopeFill className="sm-auth__label-icon" />
                Email Address
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="sm-auth__input"
              />
            </Form.Group>

            <Form.Group className="sm-auth__form-group">
              <Form.Label className="sm-auth__label">
                <KeyFill className="sm-auth__label-icon" />
                Password
              </Form.Label>
              <Form.Control
                type="password"
                placeholder={`Minimum ${PASSWORD_MIN_LENGTH} characters`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="sm-auth__input"
              />
            </Form.Group>

            <Form.Group className="sm-auth__form-group">
              <Form.Label className="sm-auth__label">
                <ShieldLockFill className="sm-auth__label-icon" />
                Confirm Password
              </Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="sm-auth__input"
              />
            </Form.Group>

            {error && <Alert className="sm-auth__alert" role="alert">{error}</Alert>}

            <Button type="submit" className="sm-auth__submit" disabled={busy} aria-busy={busy}>
              {busy ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="sm-auth__submit-spinner" />
                  Forging...
                </>
              ) : (
                '⚙ Forge Account'
              )}
            </Button>
          </Form>
        </Card.Body>
        
        <div className="sm-auth__card-line sm-auth__card-line--bottom" />
      </Card>
    </div>
  );
};

export default Register;